import React, { useEffect, useState, useRef } from "react";
import {
  LottiePlayer,
  SwitchButton,
  RadioGroup,
  DataTable,
  Button,
} from "@learningblocks/shared-components";
import {
  WidgetComponents,
  WidgetStyles,
} from "@learningblocks/shared-components/lib/WidgetComponents";
import { Header } from "./SliderComponents";
import { IntroModal } from "./IntroModal";
import { Stepper } from "./SliderComponents";
import className from "./style.module.scss";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import parse from "html-react-parser";

const { classNames: cname } = WidgetStyles;

const {
  Slider,
  SliderLabel,
  RangeSlider,
  ControlBlock,
  AnimationZoom,
  AnimationBlock,
  RangeSliderLabels,
  AnimationSlider,
} = WidgetComponents;

import UI_ar from "./locales/ar/translation.json";
import UI_de from "./locales/de/translation.json";
import UI_en from "./locales/en/translation.json";
import UI_es from "./locales/es/translation.json";
import UI_fr from "./locales/fr/translation.json";
import UI_it from "./locales/it/translation.json";
import UI_ja from "./locales/ja/translation.json";
import UI_ko from "./locales/ko/translation.json";
import UI_nl from "./locales/nl/translation.json";
import UI_pt from "./locales/pt/translation.json";
import UI_tr from "./locales/tr/translation.json";
import UI_zh from "./locales/zh/translation.json";
import UI_zu from "./locales/zu/translation.json";
import UI_uk from "./locales/uk/translation.json";
import UI_az from "./locales/az/translation.json";

export function SliderWidgetScene(props) {
  const sceneData = props.sceneData;
  if (Object.keys(sceneData.override).length !== 0) {
    sceneData.override.toggles = sceneData.override.toggles.slice(0, 2);
  }

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  let currentLang = urlParams.get("lng");
  if (currentLang === null) {
    currentLang = "en";
  }

  const {
    t,
    // i18n
  } = useTranslation("translation");

  i18next.addResourceBundle(
    "en",
    "translation", // Supported languages used
    currentLang == "ar"
      ? UI_ar
      : currentLang == "de"
        ? UI_de
        : currentLang == "en"
          ? UI_en
          : currentLang == "es"
            ? UI_es
            : currentLang == "fr"
              ? UI_fr
              : currentLang == "it"
                ? UI_it
                : currentLang == "ja"
                  ? UI_ja
                  : currentLang == "ko"
                    ? UI_ko
                    : currentLang == "nl"
                      ? UI_nl
                      : currentLang == "pt"
                        ? UI_pt
                        : currentLang == "tr"
                          ? UI_tr
                          : currentLang == "zh"
                            ? UI_zh
                            : currentLang == "uk"
                              ? UI_uk
                              : currentLang == "az"
                                ? UI_az
                                : currentLang == "zu"
                                  ? UI_zu // Add language support by importing the file above and adding a new line here: ": currentlang == lang ? UI_lang"
                                  : UI_en,
    true,
    true
  );

  if (props.stringData) {
    i18next.addResourceBundle(
      "en",
      "translation",
      props.stringData,
      true,
      true
    );
  }

  const getDefaultRadio = () => {
    if (
      Object.hasOwn(sceneData.override, "radios") &&
      Object.keys(sceneData.override).length !== 0
    ) {
      return props.sceneData.override.radios
        .map((radio) => radio.checked)
        .indexOf("TRUE");
    } else {
      return null;
    }
  };

  const [debug, setDebug] = useState(sceneData.debug === "TRUE" ? true : false);
  const [loading] = useState(false);
  const [current, setCurrent] = useState(sceneData.current);
  const [showIntro, setShowIntro] = useState(
    sceneData.intro !== "" && Object.hasOwn(sceneData, "intro")
  );
  const [firstIntro, setFistIntro] = useState(true);
  const [frame, setFrame] = useState(0);
  const [ariaInterval, setAriaInterval] = useState(0);
  const [overrideState, setOverrideState] = useState("none");
  const [firstToggle, setFirstToggle] = useState(false);
  const [secondToggle, setSecondToggle] = useState(false);
  const [radioGroup, setRadioGroup] = useState(
    getDefaultRadio() !== -1 ? getDefaultRadio() : null
  );
  const [viewType, setViewType] = useState("");
  const [viewTableData, setViewTableData] = useState(false);
  const tableWrapper = useRef();

  useEffect(() => {
    if (tableWrapper.current) {
      tableWrapper.current.scrollLeft = 0;
      tableWrapper.current.scrollTop = 0;
    }
  }, [frame]);

  useEffect(() => {
    if (
      !showIntro &&
      sceneData.intro !== "" &&
      Object.hasOwn(sceneData, "intro")
    ) {
      // only set if widget has intro and has closed the modal at least once.
      setFistIntro(false);
    }
  }, [showIntro]);

  useEffect(() => {
    if (props.allowOverflow !== true) {
      document.title = `LabXchange | ${t(`${sceneData.name}.string`)}`;
    }
    checkViewType(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setCurrent(sceneData.current);
  }, [props.animationData, props.mobileAnimationData]);

  useEffect(() => {
    if (!loading) {
      handleSlideUpdate(current);
    }
  }, [overrideState, radioGroup]);

  const timer = {};
  const throttle = (callback, interval = 15, key = "default") => {
    if (timer[key]) {
      clearTimeout(timer[key]);
      timer[key] = undefined;
    }
    timer[key] = setTimeout(callback, interval);
  };

  const checkViewType = (width) => {
    if (width <= 767) {
      setViewType("mob");
    } else if (width <= 1023) {
      setViewType("tab");
    } else {
      setViewType("web");
    }
  };

  const onResize = () => {
    throttle(checkViewType(window.innerWidth), 100);
  };

  const debugInfo = [
    { name: "Total frames", value: !loading ? props.animationData.op : 0 },
    {
      name: "Active frame range",
      value: loading
        ? "0"
        : Object.keys(sceneData.override).length === 0
          ? `${0} - ${props.animationData.op}`
          : overrideState === "none"
            ? radioGroup !== null
              ? `${sceneData.override.noneMin + sceneData.override.radios[radioGroup].min} - ${sceneData.override.noneMax + sceneData.override.radios[radioGroup].max}`
              : `${sceneData.override.noneMin} - ${sceneData.override.noneMax}`
            : overrideState === "all"
              ? radioGroup !== null
                ? `${sceneData.override.allMin + sceneData.override.radios[radioGroup].min} - ${sceneData.override.allMax + sceneData.override.radios[radioGroup].max}`
                : `${sceneData.override.allMin} - ${sceneData.override.allMax}`
              : overrideState === "first"
                ? radioGroup !== null
                  ? `${sceneData.override.toggles[0].min + sceneData.override.radios[radioGroup].min} - ${sceneData.override.toggles[0].max + sceneData.override.radios[radioGroup].max}`
                  : `${sceneData.override.toggles[0].min} - ${sceneData.override.toggles[0].max}`
                : overrideState === "second"
                  ? radioGroup !== null
                    ? `${sceneData.override.toggles[1].min + sceneData.override.radios[radioGroup].min} - ${sceneData.override.toggles[1].max + sceneData.override.radios[radioGroup].max}`
                    : `${sceneData.override.toggles[1].min} - ${sceneData.override.toggles[1].max}`
                  : "0",
    },
    { name: "Current frame", value: frame },
    {
      name: "Active slider alt-text",
      value:
        sceneData.altTextData.length > 0
          ? t(`${sceneData.altTextData[ariaInterval].sliderAltText}.string`)
          : "",
    },
    {
      name: "Active animation alt-text",
      value:
        sceneData.altTextData.length > 0
          ? t(`${sceneData.altTextData[ariaInterval].animtionAltText}.string`)
          : "",
    },
  ];

  const handleSlideUpdate = (value) => {
    let activeMin = 0;
    let activeMax = props.animationData.op;

    if (Object.keys(sceneData.override).length !== 0) {
      switch (overrideState) {
        case "none":
          if (radioGroup !== null) {
            activeMin =
              sceneData.override.noneMin +
              sceneData.override.radios[radioGroup].min;
            activeMax =
              sceneData.override.noneMax +
              sceneData.override.radios[radioGroup].max;
          } else {
            activeMin = sceneData.override.noneMin;
            activeMax = sceneData.override.noneMax;
          }
          break;
        case "all":
          if (radioGroup !== null) {
            activeMin =
              sceneData.override.allMin +
              sceneData.override.radios[radioGroup].min;
            activeMax =
              sceneData.override.allMax +
              sceneData.override.radios[radioGroup].max;
          } else {
            activeMin = sceneData.override.allMin;
            activeMax = sceneData.override.allMax;
          }
          break;
        case "first":
          if (radioGroup !== null) {
            activeMin =
              sceneData.override.toggles[0].min +
              sceneData.override.radios[radioGroup].min;
            activeMax =
              sceneData.override.toggles[0].max +
              sceneData.override.radios[radioGroup].max;
          } else {
            activeMin = sceneData.override.toggles[0].min;
            activeMax = sceneData.override.toggles[0].max;
          }
          break;
        case "second":
          if (radioGroup !== null) {
            activeMin =
              sceneData.override.toggles[1].min +
              sceneData.override.radios[radioGroup].min;
            activeMax =
              sceneData.override.toggles[1].max +
              sceneData.override.radios[radioGroup].max;
          } else {
            activeMin = sceneData.override.toggles[1].min;
            activeMax = sceneData.override.toggles[1].max;
          }
          break;
        default:
          console.log("No matched condition.");
          break;
      }
    }

    const max = sceneData.max - sceneData.min;
    const val = value - sceneData.min;
    let newFrame =
      Math.round((val / max) * (activeMax - activeMin)) + activeMin;
    if (newFrame < activeMin) {
      newFrame = activeMin;
    }
    if (newFrame > activeMax) {
      newFrame = activeMax;
    }

    sceneData.altTextData.forEach((interval, index) => {
      if (interval.min <= newFrame && newFrame <= interval.max) {
        setAriaInterval(index);
      }
    });

    setCurrent(value);
    setFrame(newFrame);
  };

  const onToggleSwitch = (key) => {
    switch (key) {
      case 0:
        switch (overrideState) {
          case "none":
            setOverrideState("first");
            break;
          case "all":
            setOverrideState("second");
            break;
          case "first":
            setOverrideState("none");
            break;
          case "second":
            setOverrideState("all");
            break;
          default:
            console.log("No matched condition.");
        }
        setFirstToggle(!firstToggle);
        break;
      case 1:
        switch (overrideState) {
          case "none":
            setOverrideState("second");
            break;
          case "all":
            setOverrideState("first");
            break;
          case "first":
            setOverrideState("all");
            break;
          case "second":
            setOverrideState("none");
            break;
          default:
            console.log("No matched condition.");
            break;
        }
        setSecondToggle(!secondToggle);
        break;
      default:
        console.log("No matched toggle.");
        break;
    }
  };

  const onRadioSwitch = (key) => {
    setRadioGroup(key);
  };

  const renderDebugInfo = () => {
    const items = Object.keys(debugInfo).map((key) => (
      <li key={key}>
        {debugInfo[key].name}: <br />
        {debugInfo[key].value}
      </li>
    ));
    return <ul className={className.debug}>{items}</ul>;
  };

  const handleLoaded = async () => {
    // Bug causing Lottie component to override the initial frame and will not change until the slider is fully changed, so here is a hacky solution:
    await handleSlideUpdate(sceneData.maxnt);
    handleSlideUpdate(current);
  };

  return (
    <>
      <div
        id="sliderWidget-Main"
        className={className.widgetWrapper}
        role="region"
        style={{ position: !props.standalone ? "relative" : null }}
      >
        <AnimationSlider
          ariaLabel={
            sceneData.altTextData.length > 0
              ? t(
                  `${sceneData.altTextData[ariaInterval].animtionAltText}.string`
                )
              : ""
          }
          allowOverflow={props.allowOverflow === true ? true : false}
        >
          <h1
            className={className.widgetHeading}
            style={{ padding: props.allowOverflow === true ? null : "6px" }}
          >
            <div>{parse(t(`${sceneData.name}.string`))}</div>
            {!firstIntro && (
              <Button
                type="primary"
                className={className.OpenBtn}
                tabIndex={0}
                onClick={() => {
                  setShowIntro(true);
                }}
              >
                {parse(t("openIntro.string"))}
              </Button>
            )}
          </h1>
          {props.tableData && viewTableData ? (
            <div ref={tableWrapper} className={cname.tableData}>
              <DataTable
                indices={[props.tableData[frame].length, 2]}
                data={props.tableData[frame]}
              />
            </div>
          ) : (
            <AnimationBlock
              src={{ wide: "", square: "", isPath: false }}
              animationData={
                viewType === "web" ||
                props.mobileAnimationData === null ||
                props.mobileAnimationData === undefined
                  ? props.animationData
                  : props.mobileAnimationData
              }
              loading={loading}
              orientation={
                viewType === "web" ||
                props.mobileAnimationData === null ||
                props.mobileAnimationData === undefined
                  ? "wide"
                  : "square"
              }
              playerComponent={
                <LottiePlayer
                  loop={false}
                  autoplay={false}
                  showContorls={false}
                  currentFrame={frame}
                  animationLoaded={() => {
                    handleLoaded();
                  }}
                  animationImages={props.animationImages}
                  animationBackground={props.sceneData.animationBackground}
                />
              }
              zoomComponent={<AnimationZoom />}
            />
          )}
          <ControlBlock>
            {!sceneData.override.disableRangeSlider ? (
              <Slider>
                {viewType !== "web" &&
                sceneData.mobileControls &&
                Object.keys(sceneData.mobileControls).length !== 0 &&
                sceneData.mobileControls.header !== "" ? (
                  <div style={{ marginTop: "12px" }}></div>
                ) : (
                  <SliderLabel
                    id={sceneData.title}
                    text={`<tag>${t(`${sceneData.textLabel}.string`)}</tag>`}
                  />
                )}
                <RangeSliderLabels
                  left={
                    sceneData.startLabel === ""
                      ? ""
                      : t(`${sceneData.startLabel}.string`)
                  }
                  middle={
                    sceneData.middleLabel === ""
                      ? ""
                      : t(`${sceneData.middleLabel}.string`)
                  }
                  right={
                    sceneData.endLabel === ""
                      ? ""
                      : t(`${sceneData.endLabel}.string`)
                  }
                />
                <RangeSlider
                  labelledby={sceneData.title}
                  min={sceneData.min}
                  max={sceneData.max}
                  step={sceneData.step}
                  value={current}
                  scaleInterval={sceneData.scaleInterval}
                  inputLabel={
                    sceneData.altTextData.length > 0
                      ? t(
                          `${sceneData.altTextData[ariaInterval].sliderAltText}.string`
                        )
                      : ""
                  }
                  onSliderChange={(value) => handleSlideUpdate(value)}
                  altTextData={sceneData.altTextData}
                  ariaInterval={ariaInterval}
                  keyboardStep={
                    sceneData.altTextData[0].keyboardStep !== "" ? "auto" : null
                  }
                />
                {viewType !== "web" ? (
                  <div style={{ marginTop: "16px" }}>
                    {sceneData.mobileControls &&
                    Object.keys(sceneData.mobileControls).length !== 0 &&
                    sceneData.mobileControls.header !== "" ? (
                      <Header
                        className="slider_header"
                        text={t(`${sceneData.mobileControls.header}.string`)}
                      />
                    ) : null}
                    <Stepper
                      currentValue={current}
                      onChange={(value) => handleSlideUpdate(value)}
                      min={sceneData.min}
                      max={sceneData.max}
                      step={sceneData.step}
                      altTextData={sceneData.altTextData}
                      ariaInterval={ariaInterval}
                      keyboardStep={
                        sceneData.altTextData[0].keyboardStep !== ""
                          ? "auto"
                          : null
                      }
                      mobileControls={
                        sceneData.mobileControls ? sceneData.mobileControls : {}
                      }
                    />
                  </div>
                ) : null}
              </Slider>
            ) : null}
            {(Object.keys(sceneData.override).length !== 0 &&
              Object.hasOwn(sceneData.override, "toggles") &&
              sceneData.override.toggles.length > 0) ||
            props.tableData ? (
              <div className={className.switchBlock}>
                {props.tableData ? (
                  <div
                    key={sceneData.override.toggles.length}
                    className={cname.switch}
                  >
                    <SwitchButton
                      className={cname.button}
                      value={"viewTableData"}
                      checked={viewTableData}
                      name={t("tableToggleLabel.string")}
                      onChange={() => {
                        setViewTableData(!viewTableData);
                      }}
                      label={t("tableToggleLabel.string")}
                      ariaLabel={t("tableToggleAria.string")}
                    />
                  </div>
                ) : null}
                {sceneData.override.toggles.map((toggle, key) => (
                  <div key={key} className={cname.switch}>
                    <SwitchButton
                      className={cname.button}
                      value={toggle.value}
                      checked={key === 0 ? firstToggle : secondToggle}
                      name={toggle.name}
                      onChange={() => {
                        onToggleSwitch(key);
                      }}
                      label={t(`${toggle.label}.string`)}
                      ariaLabel={t(`${toggle.ariaLabel}.string`)}
                      disabled={viewTableData}
                    />
                  </div>
                ))}
              </div>
            ) : null}
            {Object.keys(sceneData.override).length !== 0 &&
            Object.hasOwn(sceneData.override, "radios") &&
            sceneData.override.radios.length > 0 ? (
              <RadioGroup
                radioGroupChecked={radioGroup}
                radioGroupLabel={sceneData.override.radioGroupLabel}
                className={cname.button}
                radios={sceneData.override.radios}
                onChange={(value) => onRadioSwitch(value)}
              />
            ) : null}
          </ControlBlock>
        </AnimationSlider>
        {showIntro && (
          <IntroModal
            text={sceneData.intro}
            firstIntro={firstIntro}
            includeGraphic={sceneData.includeIntroGraphic}
            introGraphic={sceneData.introGraphic}
            standalone={props.standalone}
            closeIntroModal={() => setShowIntro(false)}
          />
        )}
      </div>
      {debug && props.stringData && !props.allowOverflow
        ? renderDebugInfo()
        : null}
      {sceneData.debug === "TRUE" &&
      props.stringData &&
      !props.allowOverflow ? (
        <div
          className={className.debugToggle}
          role="button"
          tabIndex="0"
          onClick={() => {
            setDebug(!debug);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setDebug(!debug);
            }
          }}
        >
          Debug: {debug ? "On" : "Off"}
        </div>
      ) : null}
    </>
  );
}
